import { Glyph } from "@gocardless/flux-react";
import { HTTPError } from "@gocardless/api/utils/api";
import { HTTPStatusCode } from "@gocardless/api/http/http-status-codes";
import { getErrorResponseErrors } from "@gocardless/api/utils/error";
import { Trans } from "@lingui/macro";
import { I18n } from "@lingui/core";

import { TrackingErrorOrigin } from "../consts";

import { PopularCountryCodes } from "./consts";

import { useToastNotification } from "src/hooks/useToastNotification";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { ToTranslate } from "src/components/i18n";
import {
  ReferralEmailSource,
  ReferralEmailVariation,
} from "src/common/trackingEventAttributes";
import { CountryCodeSupported, countryCodeToName } from "src/common/country";
export interface CountryData {
  code: string;
  name: string;
  flag: Glyph;
}

export const getSupportedCountries = (
  i18n: I18n,
  countries: CountryCodeSupported[]
) => {
  const supportedCountries: CountryData[] = [];

  // Add popular countries to the top of the list
  PopularCountryCodes.map((code) =>
    supportedCountries.push({
      code,
      name: countryCodeToName(i18n)[code],
      flag: `FlagRect${code}` as Glyph,
    })
  );

  countries.map((code) => {
    supportedCountries.push({
      code,
      name: countryCodeToName(i18n)[code],
      flag: `FlagRect${code}` as Glyph,
    });
  });

  return supportedCountries;
};

const ErrorMessage = {
  InternalServerError: {
    title: <Trans id="Something went wrong">Something went wrong</Trans>,
    message: (
      <Trans id="sign-up.api-error.something-went-wrong.text">
        We couldn’t complete your sign-up, please try again later.
      </Trans>
    ),
    showUnique: true,
  },
  RequestTimeout: {
    title: <Trans id="network-error.title">Network error</Trans>,
    message: (
      <Trans id="network-error.text">
        Please check your internet connection and try again.
      </Trans>
    ),
    showUnique: true,
  },
  TooManyRequests: {
    title: (
      <Trans id="optimised-signup.rate-limit-error.title">
        There was an error
      </Trans>
    ),
    message: (
      <Trans id="optimised-signup.rate-limit-error.text">
        Too many attempts, please try again in a short while
      </Trans>
    ),
    showUnique: true,
    duration: Infinity,
  },
  CommonPassword: {
    title: (
      <Trans id="optimised-signup.rate-limit-error.title">
        There was an error
      </Trans>
    ),
    message: (
      <Trans id="optimised-signup.validation-failed-error.text">
        The password entered is common one or has appeared in a data breach,
        please use another
      </Trans>
    ),
    showUnique: true,
  },
  InsecurePassword: {
    title: (
      <ToTranslate id="optimised-signup.insecure-password-error.title">
        There was an error
      </ToTranslate>
    ),
    message: (
      <ToTranslate id="optimised-signup.insecure-password-error.text">
        Password cannot contain user information such as first name, last name
        or email
      </ToTranslate>
    ),
    showUnique: true,
  },
  EmailAlreadyExists: {
    title: (
      <Trans id="optimised-signup.rate-limit-error.title">
        There was an error
      </Trans>
    ),
    message: (
      <Trans id="optimised-signup.email-exists-error.text">
        An account already exists with this email address
      </Trans>
    ),
    showUnique: true,
  },
  InvalidEmail: {
    title: (
      <ToTranslate id="optimised-signup.email-invalid-error.title">
        Email address error
      </ToTranslate>
    ),
    message: (
      <ToTranslate id="optimised-signup.email-invalid-error.text">
        Please check your email address is correct
      </ToTranslate>
    ),
    showUnique: true,
  },
  IncorrectCode: {
    title: (
      <Trans id="optimised-signup.rate-limit-error.title">
        There was an error
      </Trans>
    ),
    message: (
      <Trans id="optimised-signup.incorrect-code-error.text">
        Incorrect code, please try again
      </Trans>
    ),
    showUnique: true,
  },
  InvalidBusinessType: {
    title: (
      <Trans id="optimised-signup.invalid-business-type-error.title">
        There was an error
      </Trans>
    ),
    message: (
      <Trans id="optimised-signup.invalid-business-type-error.text">
        Business type is not a valid value. Must be one of company, individual,
        charity, partnership, trust.
      </Trans>
    ),
    showUnique: true,
  },
};

export const useHttpErrorHandler = (
  setShowSignupForm?: (arg: boolean) => void
) => {
  const { sendEvent } = useSegment();
  const { triggerErrorNotification } = useToastNotification();

  return {
    handleError: async (error: HTTPError) => {
      const errorBody = await error.response?.json();
      const errorCode = errorBody.error?.code;

      if (errorCode === HTTPStatusCode.Unauthorized) {
        triggerErrorNotification(ErrorMessage.IncorrectCode);
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "Incorrect verification code",
          code: errorCode,
          reason: errorBody.error?.message,
        });

        return;
      }

      if (errorCode === HTTPStatusCode.InternalServerError) {
        triggerErrorNotification(ErrorMessage.InternalServerError);
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "We could not complete your sign-up, please try again later.",
          code: errorCode,
          reason: errorBody.error?.message,
        });

        return;
      }

      if (errorCode === HTTPStatusCode.RequestTimeout) {
        triggerErrorNotification(ErrorMessage.RequestTimeout);
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "Please check your internet connection and try again.",
          code: errorCode,
          reason: errorBody.error?.message,
        });

        return;
      }

      if (errorCode === HTTPStatusCode.TooManyRequests) {
        triggerErrorNotification(ErrorMessage.TooManyRequests);
        sendEvent(TrackingEvent.SIGNUP_ACCOUNT_LOCKED, {
          label: "Rate limit exceeded",
          code: errorCode,
          reason: errorBody.error?.message,
        });

        return;
      }

      if (errorCode === HTTPStatusCode.NotFound) {
        // Reuse the generic Something went wrong message when the feature flag is not enabled and a 404 error is returned
        triggerErrorNotification(ErrorMessage.InternalServerError);
        sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: errorBody.error?.message,
          code: errorCode,
          reason: errorBody.error?.message,
        });

        return;
      }

      setShowSignupForm ? setShowSignupForm(true) : null;

      if (errorCode === HTTPStatusCode.UnprocessableEntity) {
        if (errorBody.error?.errors[0].message?.includes("common password")) {
          triggerErrorNotification(ErrorMessage.CommonPassword);
          sendEvent(TrackingEvent.SIGNUP_PASSWORD_FIELD_VALIDATION_FAILED, {
            label: "Common or appeared in a data leak",
            error_origin: TrackingErrorOrigin.server_side,
            code: errorCode,
            field: errorBody.error?.errors[0].field,
            reason: errorBody.error?.message,
          });
          return;
        } else if (
          errorBody.error?.errors[0].message?.includes(
            "password cannot contain user information such as first name, last name or email"
          )
        ) {
          triggerErrorNotification(ErrorMessage.InsecurePassword);
          sendEvent(TrackingEvent.SIGNUP_PASSWORD_FIELD_VALIDATION_FAILED, {
            label: errorBody.error?.errors[0].message,
            error_origin: TrackingErrorOrigin.server_side,
            code: errorCode,
            field: errorBody.error?.errors[0].field,
            reason: errorBody.error?.message,
          });
          return;
        } else if (
          [
            "Email address has already been taken",
            "has already been taken",
          ].includes(errorBody.error?.errors[0].message)
        ) {
          triggerErrorNotification(ErrorMessage.EmailAlreadyExists);
          sendEvent(TrackingEvent.SIGNUP_EMAIL_FIELD_VALIDATION_FAILED, {
            label: errorBody.error?.errors[0].message,
            error_origin: TrackingErrorOrigin.server_side,
            code: errorCode,
            field: errorBody.error?.errors[0].field,
            reason: errorBody.error?.message,
          });
          return;
        } else if (
          errorBody.error?.errors[0].message?.includes(
            "Must be one of [company, individual, charity, partnership, trust]"
          )
        ) {
          triggerErrorNotification(ErrorMessage.InvalidBusinessType);
          // TODO: Send segment event
          return;
        } else if (
          errorBody.error?.errors[0].field?.includes("email") &&
          errorBody.error?.errors[0].message?.includes("is invalid")
        ) {
          triggerErrorNotification(ErrorMessage.InvalidEmail);
          sendEvent(TrackingEvent.SIGNUP_EMAIL_FIELD_VALIDATION_FAILED, {
            label: errorBody.error?.errors[0].message,
            error_origin: TrackingErrorOrigin.server_side,
            code: errorCode,
            field: errorBody.error?.errors[0].field,
            reason: errorBody.error?.message,
          });
          return;
        }
      }

      const errors = await getErrorResponseErrors(errorBody);
      if (errors.length > 0) {
        errors.forEach((err) => {
          triggerErrorNotification({
            message: err?.message,
            showUnique: true,
          });

          sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
            label: err?.message,
            code: errorCode,
            field: err?.field,
            reason: err?.message,
          });
        });
      }
    },
    handleNoResponse: () => {
      triggerErrorNotification(ErrorMessage.InternalServerError);
    },
  };
};

export const getReferralSource = (
  defaultReferralSource: string | null,
  defaultReferralEmailVariation: string | null
) => {
  const referralsSource = defaultReferralSource
    ? ReferralEmailSource[
        defaultReferralSource as keyof typeof ReferralEmailSource
      ]
    : defaultReferralEmailVariation
      ? ReferralEmailSource["1" as keyof typeof ReferralEmailSource]
      : ReferralEmailSource["2" as keyof typeof ReferralEmailSource];

  return referralsSource;
};

export const getReferralEmailVariation = (
  defaultReferralEmailVariation: string | null
) => {
  const referralEmailVariation = defaultReferralEmailVariation
    ? ReferralEmailVariation[
        defaultReferralEmailVariation as keyof typeof ReferralEmailVariation
      ]
    : null;
  return referralEmailVariation;
};
